import React, { useMemo } from 'react'
import type { QuoteFile } from 'paintscout'
import { getImageWidth, getColumnWidth, gridPrintStyles } from './filesGridLayout'
import {
  createStyles,
  withStyles,
  Tooltip,
  makeStyles,
  Button,
  FilePreview,
  Grid,
  Typography,
  Dropzone,
  Spinner,
  useTranslation
} from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme, DropzoneProps } from '@paintscout/ui'
import { useUser } from '@paintscout/ui-business'

import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { useConnection } from 'react-detect-offline'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

import { getFiles } from '@paintscout/util/builder'
import classnames from 'classnames'
import { useQuote } from '../context'
// Legacy and Next share the same public props (only the internal styling differs), so
// reuse the single exported definition instead of duplicating it here.
import type { FilesGridProps } from './FilesGridNext'

type FilesGridPropsWithInjections = FilesGridProps & WithStyles<typeof styles>

const styles = createStyles({
  dragging: {
    zIndex: 2147483002
  },
  ...gridPrintStyles
})

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  filePreview: {
    height: '100%',
    width: '100%',
    aspectRatio: '4/3'
  },
  image: {
    objectFit: 'cover'
  },
  uploadButton: {},
  ...gridPrintStyles,
  itemInner: ({ showBorder }: { showBorder: boolean }) => {
    // Suppress native image drag / text selection / iOS callout so the press gesture
    // reaches react-sortable-hoc instead of starting a native image drag (#20637).
    const baseStyle = {
      padding: 4,
      userSelect: 'none' as const,
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      '& img': {
        WebkitUserDrag: 'none',
        userSelect: 'none'
      }
    }

    if (showBorder === false) {
      return {
        ...baseStyle
      }
    }

    return {
      // height: '100%',
      // display: 'flex',
      // alignItems: 'center',
      // justifyContent: 'center',
      border: '1px solid #efefef',
      background: 'white',
      position: 'relative',
      ...baseStyle
    }
  },
  imageWrapper: {
    height: '100%',
    width: '100%'
  },
  selected: {
    background: '#333 !important'
  },
  check: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    zIndex: 10,
    color: '#fff'
  },
  delete: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 10,
    color: '#fff',
    background: 'black'
  },
  imageWrapperItem: {
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    // maxHeight: '100%',
    overflow: 'wrap',
    overflowY: 'hidden'
  },
  item: {
    '& img': {
      aspectRatio: '4/3'
    },
    // aspectRatio: '4/3',
    '@media print': {
      pageBreakInside: 'avoid'
    }
  },
  fileCaption: {
    color: theme.palette.text.secondary
  }
}))

interface SortableFilePreviewProps extends SortableElementProps {
  file?: QuoteFile
  columns?: number
  showBorder: boolean
  classes?: any
  editable?: boolean
  onClick?: () => any
}

const SortableFilePreview: React.ComponentClass<SortableFilePreviewProps, any> = SortableElement(
  ({ file, columns, onClick, ...props }: SortableFilePreviewProps) => {
    const classes = useStyles(props)
    let transformationProps: any = {
      ...getImageWidth(columns),
      crop: 'pad',
      background: '#fff'
    }

    if ((file.type ?? 'image').startsWith('video')) {
      transformationProps = {
        ...transformationProps,
        controls: false
      }
    }

    const { columnWidth, columnClass } = getColumnWidth(columns, classes)

    return (
      <Grid
        item
        key={file.key}
        xs={12}
        sm={columnWidth}
        md={columnWidth}
        className={classnames(columnClass, classes.item)}
      >
        <Grid
          container
          alignItems="center"
          className={classnames({ [classes.itemInner]: true, [classes.selected]: file.selected })}
        >
          {file.selected && <CheckCircleIcon className={classes.check} />}
          {/* {editable && (
              <IconButton className={classes.delete}>
                <DeleteIcon />
              </IconButton>
            )} */}
          <Grid item xs={12} className={classes.imageWrapperItem}>
            <FilePreview
              classes={{
                root: classes.filePreview
              }}
              file={file}
              videoThumbnail={true}
              onClick={onClick}
              {...transformationProps}
            />
          </Grid>
          <Grid item xs={12} className={classes.imageWrapperItem}>
            {file?.title && (
              <Typography
                classes={{ root: classes.title }}
                variant="overline"
                style={{ marginTop: 8 }}
                gutterBottom={true}
              >
                {file.title}
              </Typography>
            )}
            {file?.caption && (
              <Typography
                variant="subtitle2"
                gutterBottom={true}
                className={classnames('file-caption', classes.fileCaption)}
              >
                {file.caption}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }
)

interface SortableGridContainerProps extends SortableContainerProps {
  files: QuoteFile[]
  disabled: boolean
  showUploadDropzone?: boolean
  columns: number
  editable: boolean
  showBorder: boolean
  justify: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  classes?: any
  onUpload?: (files: File[]) => any
  onClick?: (index: number) => any
  onLoadMore?: () => Promise<boolean>
}

const SortableGridContainer: React.ComponentClass<SortableGridContainerProps, any> = SortableContainer(
  ({
    files,
    disabled,
    showUploadDropzone,
    columns,
    showBorder,
    editable,
    justify,
    onUpload,
    onClick,
    onLoadMore,
    classes
  }: SortableGridContainerProps) => {
    const { online } = useConnection()
    const { quote } = useQuote()
    const { isTrial } = useUser()
    const { __ } = useTranslation()
    const [loadingMore, setLoadingMore] = React.useState(false)
    // calculate initial number of quote files so we know how many files are left without needing
    // to update the quote first
    const numQuoteFiles = useMemo(() => getFiles({ quote, attached: true }).length - files.length, [quote])

    const canAddFiles = !isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT

    return (
      <Grid container spacing={1} alignItems={'stretch'} justifyContent={justify}>
        {files.map((file, index) => (
          <SortableFilePreview
            key={file.key}
            index={index}
            file={file}
            editable={editable}
            showBorder={showBorder}
            columns={columns}
            disabled={disabled}
            onClick={() => {
              if (onClick) {
                onClick(index)
              }
            }}
          />
        ))}
        {onLoadMore && (
          <Grid item key={'loadMore'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Button
              onClick={async () => {
                setLoadingMore(true)
                await onLoadMore()
                setLoadingMore(false)
              }}
              variant="outlined"
              fullWidth
              loading={loadingMore}
              disabled={!online}
            >
              {__('Load More')}
            </Button>
          </Grid>
        )}
        {showUploadDropzone && (
          <Grid item key={'upload'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Tooltip title={!canAddFiles ? __('You must upgrade your plan to add more media') : ''}>
              <div>
                <UploadDropzone onUpload={onUpload} disabled={!canAddFiles || !online || !editable} />
              </div>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    )
  }
)

function UploadDropzone(props: { onUpload?: (files: File[]) => any } & Partial<DropzoneProps>) {
  const { __ } = useTranslation()

  return (
    <Dropzone
      text={__('Add Media')}
      onDropAccepted={props.onUpload}
      style={{ height: 'initial', marginTop: 20 }}
      {...props}
    />
  )
}

function FilesGridLegacy(props: FilesGridPropsWithInjections) {
  const {
    files,
    editable,
    showUploadDropzone,
    onUpload,
    columns = 4,
    showBorder = true,
    justify = 'flex-start',
    classes,
    onReorder,
    onFileClick,
    isLoading,
    onLoadMore,
    useWindowAsScrollContainer = false
  } = props

  function handleSortEnd({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    const reorderedFiles = arrayMove(files, oldIndex, newIndex)

    if (onReorder) {
      onReorder(reorderedFiles)
    }
  }

  if (isLoading) {
    return <Spinner padded />
  }

  return (
    <>
      <SortableGridContainer
        classes={classes}
        helperClass={classes.dragging}
        pressDelay={150}
        useWindowAsScrollContainer={useWindowAsScrollContainer}
        files={files}
        editable={editable}
        axis="xy"
        columns={columns}
        showBorder={showBorder}
        justify={justify}
        onSortEnd={handleSortEnd}
        disabled={!editable}
        onClick={onFileClick}
        showUploadDropzone={showUploadDropzone}
        onUpload={onUpload}
        onLoadMore={onLoadMore}
      />
    </>
  )
}

export default withStyles(styles)(FilesGridLegacy)
